'use client';

import Link from "next/link"
import { Button } from "@/components/ui/button"
import { BecomePreceptorFormCompact } from "@/modules/preceptor/components/become-preceptor-form-compact"
import {
  ArrowRight,
  ShieldCheck,
  Sparkles,
  HeartPulse,
  Clock3,
  GraduationCap,
  Users2,
  MapPin,
  Star,
  FileBadge,
  Stethoscope,
  Plus,
  Minus,
} from "lucide-react"
import { Animated, StaggerContainer } from '@/components/ui/animated'

export default function BecomePreceptorPage() {
  const heroHighlights = [
    {
      icon: ShieldCheck,
      title: "Verified students",
      description: "We pair you with screened programs and ready-to-learn students.",
    },
    {
      icon: Clock3,
      title: "Your schedule, your way",
      description: "Set availability windows, preferred rotation length, and volume.",
    },
    {
      icon: HeartPulse,
      title: "Meaningful impact",
      description: "Shape clinicians who expand access to high-quality care.",
    },
  ]

  const valueCards = [
    {
      icon: Sparkles,
      title: "Concierge support",
      description: "A dedicated coordinator helps with onboarding, documentation, and communication.",
    },
    {
      icon: FileBadge,
      title: "Streamlined onboarding",
      description: "We collect requirements once and keep them updated for future rotations.",
    },
    {
      icon: Star,
      title: "Recognition that travels",
      description: "Build a mentor profile that highlights your expertise and interests.",
    },
    {
      icon: MapPin,
      title: "Local or telehealth",
      description: "Host in-person rotations or offer virtual mentorship when it fits.",
    },
  ]

  const howItWorks = [
    {
      title: "Share your practice details",
      description: "Tell us about your setting, schedule preferences, and student capacity.",
    },
    {
      title: "Meet your match",
      description: "We introduce pre-vetted students that fit your specialty and availability.",
    },
    {
      title: "Mentor with support",
      description: "We provide evaluation templates, check-ins, and payment coordination.",
    },
  ]

  const credentialTitles = [
    "DO - Doctor of Osteopathic Medicine",
    "MD - Medical Doctor",
    "PhD - Doctor of Philosophy",
    "PMHNP - Psychiatric Mental Health Nurse Practitioner",
    "FNP - Family Nurse Practitioner",
    "AGACNP - Adult Gerontology Acute Care Nurse Practitioner",
    "AGPCNP - Adult Gerontology Primary Care Nurse Practitioner",
    "AGNP - Adult Gerontology Nurse Practitioner",
    "PNP - Pediatric Nurse Practitioner",
    "WHNP - Women's Health Nurse Practitioner",
    "PA - Physician Assistant",
    "IMG - International Medical Graduate",
  ]

  const faqs = [
    {
      q: "What credentials do I need?",
      a: "Active licensure, an established practice, and current clinical experience. We verify documentation during onboarding.",
    },
    {
      q: "How much time does it take?",
      a: "You decide. Select days, rotation length, and number of students. Pause or adjust anytime.",
    },
    {
      q: "Do I get compensated?",
      a: "Yes. We work with programs to align on honorarium and payment cadence that matches your expectations.",
    },
    {
      q: "What support is included?",
      a: "Templates for evaluations, coordinator check-ins, and quick help if issues arise during a rotation.",
    },
  ]

  return (
    <div className="flex flex-col bg-white">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-indigo-900 via-purple-800 to-blue-700 text-white">
        <div className="absolute inset-0">
          <div className="absolute -top-24 -right-12 h-80 w-80 rounded-full bg-purple-400/20 blur-3xl" />
          <div className="absolute bottom-0 left-10 h-80 w-80 rounded-full bg-blue-300/20 blur-3xl" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(255,255,255,0.12),transparent_40%)]" />
        </div>

        <div className="section-inner relative grid items-center gap-12 py-20 lg:grid-cols-[1.05fr,0.95fr]">
          <Animated animation="fade-right" duration={800}>
            <div className="space-y-8">
              <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-2 text-sm font-semibold text-purple-50 ring-1 ring-white/20">
                <Stethoscope className="h-4 w-4" />
                Preceptor Applications
              </div>
              <div className="space-y-4">
                <h1 className="text-4xl font-bold leading-tight tracking-tight text-white md:text-5xl">
                  Mentor the next generation
                </h1>
                <p className="max-w-2xl text-lg text-purple-50/90">
                  Welcome motivated students into your practice. You set the schedule and scope; we handle matching, onboarding, and support.
                </p>
              </div>

              <StaggerContainer staggerDelay={100} animation="fade-up" className="grid gap-4 sm:grid-cols-2">
                {heroHighlights.map((item) => {
                  const Icon = item.icon
                  return (
                    <div
                      key={item.title}
                      className="flex gap-3 rounded-xl bg-white/5 p-4 ring-1 ring-white/10 backdrop-blur hover:bg-white/10 transition-colors duration-300"
                    >
                      <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-white/10 text-white">
                        <Icon className="h-5 w-5" />
                      </div>
                      <div className="space-y-1">
                        <p className="text-sm font-semibold text-white">{item.title}</p>
                        <p className="text-xs text-purple-50/80">{item.description}</p>
                      </div>
                    </div>
                  )
                })}
              </StaggerContainer>

              <div className="flex flex-wrap items-center gap-4">
                <Link href="#form">
                  <Button className="bg-white px-5 py-2.5 text-purple-800 shadow-lg shadow-purple-900/20 hover:bg-purple-50 hover:scale-105 transition-transform">
                    Get started
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
                <div className="flex items-center gap-3 text-sm text-purple-50/80">
                  <ShieldCheck className="h-4 w-4" />
                  No cost to join. You decide when to accept students.
                </div>
              </div>
            </div>
          </Animated>

          {/* Form */}
          <Animated animation="fade-left" delay={200} duration={800}>
            <div
              id="form"
              className="rounded-2xl border border-white/20 bg-white text-slate-900 shadow-2xl shadow-purple-900/30"
            >
            <div className="space-y-2 border-b border-slate-100 px-8 py-6">
              <div className="inline-flex items-center gap-2 rounded-full bg-purple-50 px-3 py-1 text-xs font-semibold text-purple-700">
                <Users2 className="h-4 w-4" />
                Join our mentor network
              </div>
              <h2 className="text-2xl font-bold text-slate-900">Tell us about your practice</h2>
              <p className="text-sm text-slate-600">
                Share your background and availability. We&apos;ll match you with vetted PA and NP programs.
              </p>
              <div className="flex flex-wrap gap-3 text-xs text-slate-600">
                <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1">
                  <GraduationCap className="h-3.5 w-3.5" />
                  Academic partnerships
                </span>
                <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1">
                  <Clock3 className="h-3.5 w-3.5" />
                  Flexible rotations
                </span>
                <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1">
                  <ShieldCheck className="h-3.5 w-3.5" />
                  Dedicated support
                </span>
              </div>
            </div>
            <div className="px-8 py-6">
              <BecomePreceptorFormCompact />
            </div>
          </div>
          </Animated>
        </div>
      </section>

      {/* Value Section */}
      <section className="bg-white py-16">
        <div className="section-inner space-y-10">
          <Animated animation="fade-up">
            <div className="flex flex-col gap-3 text-center">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-purple-700">What you get</p>
              <h2 className="text-3xl font-bold text-slate-900">A smoother way to host students</h2>
              <p className="mx-auto max-w-3xl text-base text-slate-600">
                From documentation to ongoing check-ins, we keep the logistics light so you can focus on mentoring.
              </p>
            </div>
          </Animated>
          <StaggerContainer staggerDelay={100} animation="fade-up" className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {valueCards.map((item) => {
              const Icon = item.icon
              return (
                <div
                  key={item.title}
                  className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm shadow-purple-900/5 transition-all duration-300 hover:shadow-lg hover:-translate-y-1"
                >
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-purple-50 text-purple-700">
                    <Icon className="h-5 w-5" />
                  </div>
                  <h3 className="mt-3 text-lg font-semibold text-slate-900">{item.title}</h3>
                  <p className="mt-2 text-sm text-slate-600">{item.description}</p>
                </div>
              )
            })}
          </StaggerContainer>
        </div>
      </section>

      {/* How It Works */}
      <section className="bg-gray-50 py-16">
        <div className="section-inner space-y-12">
          <Animated animation="fade-up">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-purple-700">How it works</p>
                <h2 className="mt-2 text-3xl font-bold text-slate-900">A simple path from interest to mentorship</h2>
                <p className="mt-2 max-w-2xl text-sm text-slate-600">
                  We keep you in control of timing, capacity, and the types of students you want to teach.
                </p>
              </div>
              <Link href="#form">
                <Button variant="outline" className="border-purple-200 text-purple-800 hover:bg-purple-50 hover:scale-105 transition-transform">
                  Start your profile
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
          </Animated>

          <StaggerContainer staggerDelay={150} animation="fade-up" className="grid gap-6 md:grid-cols-3">
            {howItWorks.map((step, index) => (
              <div
                key={step.title}
                className="relative rounded-2xl border border-purple-100 bg-white p-6 shadow-sm shadow-purple-900/5 hover:shadow-lg transition-all duration-300"
              >
                <div className="absolute -top-4 flex h-10 w-10 items-center justify-center rounded-full bg-purple-600 text-white shadow-lg shadow-purple-900/20">
                  {index + 1}
                </div>
                <div className="mt-4 space-y-2">
                  <h3 className="text-lg font-semibold text-slate-900">{step.title}</h3>
                  <p className="text-sm text-slate-600">{step.description}</p>
                </div>
              </div>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* Credentials */}
      <section className="bg-white py-16">
        <div className="section-inner space-y-6">
          <Animated animation="fade-up">
            <div className="flex flex-col gap-3 text-center">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-purple-700">Credentials we see often</p>
              <h2 className="text-3xl font-bold text-slate-900">Common titles across our preceptor network</h2>
              <p className="mx-auto max-w-3xl text-sm text-slate-600">
                Select all that apply in the form. We welcome diverse backgrounds and interprofessional collaboration.
              </p>
            </div>
          </Animated>
          <Animated animation="fade-up" delay={200}>
            <div className="flex flex-wrap gap-3 justify-center">
              {credentialTitles.map((title, index) => (
                <span
                  key={title}
                  className="inline-flex items-center rounded-full border border-purple-100 bg-purple-50 px-3 py-1 text-xs font-medium text-purple-800 hover:bg-purple-100 hover:scale-105 transition-all duration-200 cursor-default"
                  style={{ animationDelay: `${index * 50}ms` }}
                >
                  {title}
                </span>
              ))}
            </div>
          </Animated>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="bg-gray-50 py-16">
        <div className="section-inner max-w-3xl space-y-8">
          <Animated animation="fade-up">
            <div className="space-y-2 text-center">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-purple-700">Questions</p>
              <h2 className="text-3xl font-bold text-slate-900">Common questions from preceptors</h2>
              <p className="text-sm text-slate-600">
                Still unsure? Reach out after you apply and we&apos;ll tailor the process to your preferences.
              </p>
            </div>
          </Animated>
          <StaggerContainer staggerDelay={100} animation="fade-up" className="space-y-3">
            {faqs.map((faq) => (
              <details
                key={faq.q}
                className="group rounded-xl border border-slate-200 bg-white p-5 shadow-sm shadow-purple-900/5 hover:shadow-md transition-all duration-300"
              >
                <summary className="flex cursor-pointer items-center justify-between gap-3 text-left text-sm font-semibold text-slate-900 transition-colors group-open:text-purple-700">
                  {faq.q}
                  <span className="flex h-6 w-6 items-center justify-center rounded-full border border-slate-200 text-slate-500 group-open:border-purple-200 group-open:bg-purple-50 group-open:text-purple-700 transition-all duration-300">
                    <Plus className="h-3 w-3 group-open:hidden" />
                    <Minus className="hidden h-3 w-3 group-open:block" />
                  </span>
                </summary>
                <p className="mt-3 text-sm leading-relaxed text-slate-600 animate-in fade-in slide-in-from-top-2 duration-300">{faq.a}</p>
              </details>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative overflow-hidden bg-gradient-to-r from-purple-700 to-blue-600 py-16 text-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(255,255,255,0.15),transparent_40%)]" />
        <div className="section-inner relative flex flex-col items-center gap-4 text-center">
          <Animated animation="fade-up">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-purple-100">Ready to mentor?</p>
          </Animated>
          <Animated animation="fade-up" delay={100}>
            <h2 className="text-3xl font-bold leading-tight text-white md:text-4xl">
              Join a community of clinicians shaping tomorrow&apos;s providers
            </h2>
          </Animated>
          <Animated animation="fade-up" delay={200}>
            <p className="max-w-3xl text-base text-purple-50/90">
              Let us know your preferences and we&apos;ll bring the right students to you on your timeline.
            </p>
          </Animated>
          <Animated animation="scale" delay={300}>
            <Link href="#form">
              <Button className="bg-white text-purple-800 hover:bg-purple-50 hover:scale-105 transition-transform">
                Start your application
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </Animated>
        </div>
      </section>
    </div>
  )
}
